import { useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../design-v2";
import AuthLayout from "../AuthLayout";
import ProviderButton, {
  readLastUsedAuthProvider,
} from "../components/ProviderButton";
import { getAuthenticatedDestination } from "../authRouting";
import useAuthBootstrap from "../useAuthBootstrap";
import { resolveAnonymousEntry } from "../../features/onboarding/anonymousEntry";
import { loadAnonymousOnboardingDraft } from "../../features/onboarding/onboardingDraftStore";
import {
  getAuthProviderLabel,
  getStartableAuthProviders,
} from "../providerCapabilities";
import { startProviderAuthentication } from "../oauthStart";

// `auth=cancelled` deliberately produces no copy: cancelling a sign-in is a
// normal outcome, not a failure to apologise for.
function authErrorCopy(search) {
  const params = new URLSearchParams(search);
  const code = params.get("auth");
  if (code === "provider_error") {
    const provider = getAuthProviderLabel(params.get("provider")) || "your sign-in provider";
    return `We couldn't reach ${provider}. Please try again.`;
  }
  if (code === "state_invalid") {
    return "Let's try that again.";
  }
  return "";
}

export default function AccountGateScreen() {
  const bootstrap = useAuthBootstrap();
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingProvider, setPendingProvider] = useState(null);
  const lastUsedProvider = useMemo(() => readLastUsedAuthProvider(), []);
  const startableProviders = getStartableAuthProviders(bootstrap.providers);
  const error = authErrorCopy(location.search);

  if (bootstrap.status === "authenticated") {
    return <Navigate to={getAuthenticatedDestination(bootstrap.onboarding)} replace />;
  }

  const anonymousEntry = resolveAnonymousEntry({ draft: loadAnonymousOnboardingDraft() });
  if (anonymousEntry.reason !== "ACCOUNT_GATE") {
    return <Navigate to={anonymousEntry.destination} replace />;
  }

  const selectProvider = (providerId) => {
    setPendingProvider(providerId);
    if (providerId === "email") {
      navigate("/auth/email", { state: { authSurface: "/auth" } });
      return;
    }
    if (!startProviderAuthentication({ providerId, returnTo: "/", surface: "/auth" })) {
      setPendingProvider(null);
    }
  };

  return (
    <AuthLayout>
      <section className="lz-auth-provider-screen" aria-labelledby="account-gate-title">
        <div className="lz-auth-provider-screen__intro lz-auth-intro">
          <h1 id="account-gate-title">
            UNLEASH YOUR<br /><span>PROGRAM.</span>
          </h1>
          <p>Create an account to architect and save your personalized 6-week training program.</p>
        </div>

        <div className="lz-auth-provider-screen__actions">
          {startableProviders.map((providerId) => (
            <ProviderButton
              key={providerId}
              providerId={providerId}
              isLastUsed={providerId === lastUsedProvider}
              isLoading={pendingProvider === providerId}
              disabled={Boolean(pendingProvider)}
              onClick={() => selectProvider(providerId)}
            />
          ))}
        </div>

        {error ? <p className="lz-auth-provider-screen__error" role="alert">{error}</p> : null}

        <p className="lz-auth-provider-screen__secondary">
          Already have an account?{" "}
          <Button variant="ghost" onClick={() => navigate("/login")}>Log in</Button>
        </p>
      </section>
    </AuthLayout>
  );
}

export { authErrorCopy };
